import React, { useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { CREATE_EMPLOYER } from "../../utils/constants";
import useRequireAuth from "../../utils/useRequireAuth";
import { useNavigate } from "react-router-dom";
import LayoutHOC from "../LayoutHOC";
import { Link } from "react-router-dom";
import useCompany from "../../hooks/useCompany";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";

const CreateEmployer = () => {
  const user = useRequireAuth();
  const navigate = useNavigate();
  const company = useCompany();
  const [selectedDate, setSelectedDate] = useState(null);
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  const initialValues = {
    name: "",
    company_name: "",
    email: "",
    mobile: "",
    company_size: "",
    organizationType: "",
    industryTypeId: "",
    website: "",
    establishmentYear: "",
    about: "",
    password: "",
  };

  const validationSchema = Yup.object().shape({
    name: Yup.string().required("Name is required"),
    company_name: Yup.string().required("Company name is required"),
    email: Yup.string()
      .email("Invalid email format")
      .required("Email is required"),
    mobile: Yup.string().required("Mobile number is required"),
    company_size: Yup.string().required("Company size is required"),
    organizationType: Yup.string().required("Organization type is required"),
    industryTypeId: Yup.string().required("Industry type is required"),
    establishmentYear: Yup.string().required("Establishment year is required"),
    password: Yup.string().required("Password is required"),
  });

  const handleSubmit = async (values, { setSubmitting, setFieldError }) => {
    try {
      const Authtoken = user.token;
      const formDataWithFile = new FormData();

      Object.keys(values).forEach((key) => {
        formDataWithFile.append(key, values[key]);
      });

      if (file) {
        formDataWithFile.append("companylogo", file);
      }

      const response = await fetch(CREATE_EMPLOYER, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${Authtoken}`,
        },
        body: formDataWithFile,
      });

      if (!response.ok) {
        throw new Error("Failed to create employer");
      }

      navigate(`/employer/`);
      setSubmitting(false);
    } catch (error) {
      setFieldError("form", "An error occurred while submitting the form.");
      setSubmitting(false);
    }
  };

  const handleFileChange = (event) => {
    const selectedFile = event.currentTarget.files[0];
    setFile(selectedFile);
    if (selectedFile) {
      const url = URL.createObjectURL(selectedFile);
      setPreviewUrl(url);
    }
  };

  return (
    <main className="p-4">
      <div className="p-4 mt-14">
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-violet-50 font-poppins">
          <div className="bg-gradient-to-r from-violet-600 via-purple-600 to-violet-700 rounded-xl shadow-xl mb-6 overflow-hidden">
            <div className="px-8 py-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <Link to="/employer" className="p-2 bg-white/20 rounded-lg hover:bg-white/30 transition-colors">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                    </svg>
                  </Link>
                  <div>
                    <h1 className="text-2xl font-bold text-white">Create New Employer</h1>
                    <p className="text-violet-100 text-sm mt-1">Register a new company in the system</p>
                  </div>
                </div>
                <div className="hidden md:flex items-center gap-2 bg-white/20 px-4 py-2 rounded-lg">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                  <span className="text-white font-medium">Company Registration</span>
                </div>
              </div>
            </div>
          </div>

          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ isSubmitting, setFieldValue }) => (
              <Form id="companyProfileForm" encType="multipart/form-data">
                <div className="bg-white rounded-xl shadow-lg border border-gray-100 mb-6 overflow-hidden">
                  <div className="bg-gradient-to-r from-violet-50 to-purple-50 px-6 py-4 border-b border-violet-100">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-violet-100 rounded-lg">
                        <svg className="w-5 h-5 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                      </div>
                      <h3 className="text-lg font-semibold text-gray-800">Company Information</h3>
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      <Field name="name">
                        {({ field }) => (
                          <div>
                            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                              Employer Name <span className="text-red-500">*</span>
                            </label>
                            <input
                              {...field}
                              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-all duration-200"
                              type="text"
                              placeholder="Enter employer name"
                            />
                            <ErrorMessage name="name" component="div" className="text-red-500 text-sm mt-1" />
                          </div>
                        )}
                      </Field>

                      <Field name="company_name">
                        {({ field }) => (
                          <div>
                            <label htmlFor="company_name" className="block text-sm font-medium text-gray-700 mb-2">
                              Company Name <span className="text-red-500">*</span>
                            </label>
                            <input
                              {...field}
                              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-all duration-200"
                              type="text"
                              placeholder="Enter company name"
                            />
                            <ErrorMessage name="company_name" component="div" className="text-red-500 text-sm mt-1" />
                          </div>
                        )}
                      </Field>

                      <Field name="email">
                        {({ field }) => (
                          <div>
                            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                              Email Address <span className="text-red-500">*</span>
                            </label>
                            <input
                              {...field}
                              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-all duration-200"
                              type="email"
                              placeholder="Enter email address"
                            />
                            <ErrorMessage name="email" component="div" className="text-red-500 text-sm mt-1" />
                          </div>
                        )}
                      </Field>

                      <Field name="mobile">
                        {({ field }) => (
                          <div>
                            <label htmlFor="mobile" className="block text-sm font-medium text-gray-700 mb-2">
                              Mobile Number <span className="text-red-500">*</span>
                            </label>
                            <input
                              {...field}
                              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-all duration-200"
                              type="text"
                              placeholder="Enter mobile number"
                            />
                            <ErrorMessage name="mobile" component="div" className="text-red-500 text-sm mt-1" />
                          </div>
                        )}
                      </Field>

                      <Field name="company_size">
                        {({ field }) => (
                          <div>
                            <label htmlFor="company_size" className="block text-sm font-medium text-gray-700 mb-2">
                              Company Size <span className="text-red-500">*</span>
                            </label>
                            <input
                              {...field}
                              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-all duration-200"
                              type="number"
                              placeholder="Enter company size (e.g. 50)"
                              min="0"
                            />
                            <ErrorMessage name="company_size" component="div" className="text-red-500 text-sm mt-1" />
                          </div>
                        )}
                      </Field>

                      <Field name="organizationType">
                        {({ field }) => (
                          <div>
                            <label htmlFor="organizationType" className="block text-sm font-medium text-gray-700 mb-2">
                              Organization Type <span className="text-red-500">*</span>
                            </label>
                            <select
                              {...field}
                              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-all duration-200"
                            >
                              <option value="">Select Organization Type</option>
                              <option value="Private">Private Limited</option>
                              <option value="Public">Public Limited</option>
                              <option value="Partnership">Partnership</option>
                              <option value="Sole Proprietorship">Sole Proprietorship</option>
                              <option value="LLP">LLP</option>
                              <option value="NGO">NGO</option>
                              <option value="Government">Government</option>
                            </select>
                            <ErrorMessage name="organizationType" component="div" className="text-red-500 text-sm mt-1" />
                          </div>
                        )}
                      </Field>

                      <Field name="industryTypeId">
                        {({ field }) => (
                          <div>
                            <label htmlFor="industryTypeId" className="block text-sm font-medium text-gray-700 mb-2">
                              Industry Type <span className="text-red-500">*</span>
                            </label>
                            <Field
                              as="select"
                              id="industryTypeId"
                              name="industryTypeId"
                              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-all duration-200"
                            >
                              <option value="">Select Industry Type</option>
                              {company?.data?.industry_type?.map((industryType) => (
                                <option key={industryType.id} value={industryType.id}>
                                  {industryType.name}
                                </option>
                              ))}
                            </Field>
                            <ErrorMessage name="industryTypeId" component="div" className="text-red-500 text-sm mt-1" />
                          </div>
                        )}
                      </Field>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-xl shadow-lg border border-gray-100 mb-6 overflow-hidden">
                  <div className="bg-gradient-to-r from-purple-50 to-violet-50 px-6 py-4 border-b border-purple-100">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-purple-100 rounded-lg">
                        <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <h3 className="text-lg font-semibold text-gray-800">Additional Details</h3>
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      <Field name="website">
                        {({ field }) => (
                          <div>
                            <label htmlFor="website" className="block text-sm font-medium text-gray-700 mb-2">
                              Website
                            </label>
                            <input
                              {...field}
                              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-all duration-200"
                              type="text"
                              placeholder="https://www.example.com"
                            />
                            <ErrorMessage name="website" component="div" className="text-red-500 text-sm mt-1" />
                          </div>
                        )}
                      </Field>

                      <Field name="establishmentYear">
                        {({ field }) => (
                          <div>
                            <label htmlFor="establishmentYear" className="block text-sm font-medium text-gray-700 mb-2">
                              Establishment Year <span className="text-red-500">*</span>
                            </label>
                            <DatePicker
                              selected={selectedDate}
                              onChange={(date) => {
                                setSelectedDate(date);
                                const formattedDate = moment(date).format("YYYY-MM-DD");
                                setFieldValue("establishmentYear", formattedDate);
                              }}
                              dateFormat="yyyy-MM-dd"
                              showYearPicker
                              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-all duration-200"
                              placeholderText="Select establishment year"
                            />
                            <ErrorMessage name="establishmentYear" component="div" className="text-red-500 text-sm mt-1" />
                          </div>
                        )}
                      </Field>

                      <Field name="password">
                        {({ field }) => (
                          <div>
                            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                              Password <span className="text-red-500">*</span>
                            </label>
                            <input
                              {...field}
                              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-all duration-200"
                              type="password"
                              placeholder="Enter password"
                            />
                            <ErrorMessage name="password" component="div" className="text-red-500 text-sm mt-1" />
                          </div>
                        )}
                      </Field>

                      <Field name="about">
                        {({ field }) => (
                          <div className="lg:col-span-3">
                            <label htmlFor="about" className="block text-sm font-medium text-gray-700 mb-2">
                              About Company
                            </label>
                            <textarea
                              {...field}
                              rows="4"
                              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-all duration-200"
                              placeholder="Write a brief description about the company..."
                            />
                            <ErrorMessage name="about" component="div" className="text-red-500 text-sm mt-1" />
                          </div>
                        )}
                      </Field>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-xl shadow-lg border border-gray-100 mb-6 overflow-hidden">
                  <div className="bg-gradient-to-r from-fuchsia-50 to-violet-50 px-6 py-4 border-b border-fuchsia-100">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-fuchsia-100 rounded-lg">
                        <svg className="w-5 h-5 text-fuchsia-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <h3 className="text-lg font-semibold text-gray-800">Company Logo</h3>
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="flex items-start gap-6">
                      <div className="flex-shrink-0">
                        {previewUrl ? (
                          <img
                            src={previewUrl}
                            alt="Company Logo Preview"
                            className="w-32 h-32 object-cover rounded-lg border-2 border-gray-200"
                          />
                        ) : (
                          <div className="w-32 h-32 bg-gray-100 rounded-lg flex items-center justify-center border-2 border-dashed border-gray-300">
                            <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                            </svg>
                          </div>
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="border-2 border-dashed border-gray-200 rounded-lg p-6 text-center hover:border-violet-400 transition-colors">
                          <input
                            id="companylogo"
                            name="companylogo"
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={handleFileChange}
                          />
                          <label htmlFor="companylogo" className="cursor-pointer">
                            <svg className="w-10 h-10 text-gray-400 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                            </svg>
                            <p className="text-sm text-gray-500">Click to upload company logo</p>
                            <p className="text-xs text-gray-400 mt-1">PNG, JPG, SVG up to 5MB</p>
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex justify-between items-center bg-white rounded-xl shadow-lg border border-gray-100 p-6">
                  <Link to="/employer">
                    <button
                      type="button"
                      className="px-6 py-3 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 transition-colors flex items-center gap-2"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                      Cancel
                    </button>
                  </Link>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="px-8 py-3 bg-gradient-to-r from-violet-600 to-purple-600 text-white font-medium rounded-lg hover:from-violet-700 hover:to-purple-700 transition-all shadow-lg hover:shadow-xl flex items-center gap-2 disabled:opacity-50"
                  >
                    {isSubmitting ? (
                      <>
                        <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Creating...
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                        Create Employer
                      </>
                    )}
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </main>
  );
};

export default LayoutHOC(CreateEmployer);
